import {useMemo, useState} from 'react'

import {validateValue} from '../components/validate'

export interface PropertyField {
  value: string
  mixed: boolean
  changed: boolean
  propertyName: string
  error?: string
}

export function useEditCustomProperties(reposProperties: Array<Record<string, string>>) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't expect properties to change
  const mergedValues = useMemo(() => mergeProperties(reposProperties), [])

  const [modifiedProperties, setEditingProperties] = useState<Record<string, string>>({})

  function setPropertyValue(property: string, value: string) {
    setEditingProperties({...modifiedProperties, [property]: value})
  }

  function revertPropertyValue(property: string) {
    const updatedProperties = {...modifiedProperties}
    delete updatedProperties[property]
    setEditingProperties(updatedProperties)
  }

  const propertyValuesMap = useMemo<Record<string, PropertyField>>(() => {
    return Object.keys({...mergedValues, ...modifiedProperties}).reduce<Record<string, PropertyField>>(
      (acc, propertyName) => {
        const isModified = propertyName in modifiedProperties
        const originalValue = mergedValues[propertyName] || {value: ''}

        const mixed = isModified ? false : originalValue.mixed

        if (mixed) {
          acc[propertyName] = {propertyName, changed: false, mixed: true, value: ''}
        } else {
          const modifiedValue = modifiedProperties[propertyName]
          const value = (isModified ? modifiedValue : originalValue.value) || ''
          const changed = isModified && (originalValue.mixed || originalValue.value !== modifiedValue)

          const newEntry: PropertyField = {value, changed, propertyName, mixed: false}

          const error = isModified ? validateValue(modifiedValue || '') : undefined
          if (error) {
            newEntry.error = error
          }

          acc[propertyName] = newEntry
        }

        return acc
      },
      {},
    )
  }, [modifiedProperties, mergedValues])

  return {propertyValuesMap, setPropertyValue, revertPropertyValue}
}

interface MergedValue {
  value: string
  mixed?: true
}

function mergeProperties(reposProperties: Array<Record<string, string>>): Record<string, MergedValue> {
  const mergedValues: Record<string, MergedValue> = {}
  const propertiesCounter: Record<string, number> = {}
  for (const repoProperties of reposProperties) {
    for (const [propertyName, value] of Object.entries(repoProperties)) {
      const propertyValue = mergedValues[propertyName]
      if (!propertyValue) {
        mergedValues[propertyName] = {value}
      } else if (propertyValue.value !== value) {
        mergedValues[propertyName] = {value: '', mixed: true}
      }

      // Detect properties that are only present on some repos to consider them in the "mixed" state
      propertiesCounter[propertyName] ||= 0
      propertiesCounter[propertyName]++
    }
  }

  for (const [name, count] of Object.entries(propertiesCounter)) {
    const propertyValue = mergedValues[name]
    if (propertyValue && count < reposProperties.length) {
      mergedValues[name] = {value: '', mixed: true}
    }
  }

  return mergedValues
}
